import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  Send,
  MessageSquare,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Phone,
  User,
  Sparkles,
  Info,
  Layers,
  Clock,
  History,
  ExternalLink,
  Copy,
  Check,
} from 'lucide-react';
import { CompanyInfo, ServiceTicket, SmsLog, SmsSettings } from '../types';
import {
  formatSmsTemplate,
  getLocalSmsSettings,
  sendSmsMessage,
  checkSmsBalance,
  getLocalSmsLogs,
  getCustomerTrackingUrl,
} from '../utils/smsService';
import { formatDateTR } from '../utils/storage';

interface SmsModalProps {
  ticket: ServiceTicket | null;
  isOpen: boolean;
  onClose: () => void;
  companyInfo: CompanyInfo;
  onOpenSettings?: () => void;
  isTechnician?: boolean;
}

export const SmsModal: React.FC<SmsModalProps> = ({
  ticket,
  isOpen,
  onClose,
  companyInfo,
  onOpenSettings,
  isTechnician = false,
}) => {
  if (!isOpen || !ticket) return null;

  const [settings, setSettings] = useState<SmsSettings>(getLocalSmsSettings());
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string>('ticketReady');
  const [phone, setPhone] = useState<string>(ticket.customerPhone || '');
  const [message, setMessage] = useState<string>('');
  const [sending, setSending] = useState<boolean>(false);
  const [statusResult, setStatusResult] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const [balanceInfo, setBalanceInfo] = useState<string | null>(null);
  const [checkingBalance, setCheckingBalance] = useState<boolean>(false);
  const [showLogs, setShowLogs] = useState<boolean>(false);
  const [ticketLogs, setTicketLogs] = useState<SmsLog[]>([]);
  const [copiedTrackingLink, setCopiedTrackingLink] = useState<boolean>(false);

  const directTrackingUrl = useMemo(() => {
    return getCustomerTrackingUrl(ticket.id, phone || ticket.customerPhone);
  }, [ticket.id, phone, ticket.customerPhone]);

  const handleCopyDirectTrackingLink = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(directTrackingUrl);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = directTrackingUrl;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiedTrackingLink(true);
      setTimeout(() => setCopiedTrackingLink(false), 2500);
    } catch (e) {
      console.error('Failed to copy link', e);
    }
  };

  const handleOpenDirectTracking = () => {
    if (typeof window !== 'undefined') {
      window.open(directTrackingUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Update initial message when template or ticket changes
  useEffect(() => {
    const curSettings = getLocalSmsSettings();
    setSettings(curSettings);
    setPhone(ticket.customerPhone || '');

    // Choose default template based on ticket status
    let defaultKey = 'ticketReady';
    if (ticket.status === 'TeslimEdildi') {
      defaultKey = 'ticketDelivered';
    } else if (ticket.status === 'Kaydedildi') {
      defaultKey = 'ticketCreated';
    } else if (ticket.status === 'IadeEdildi' || ticket.status === 'Iptal') {
      defaultKey = 'ticketCancelled';
    }
    setSelectedTemplateKey(defaultKey);

    const templateText = (curSettings.templates as any)[defaultKey] || curSettings.templates.ticketReady;
    const formatted = formatSmsTemplate(templateText, ticket, companyInfo);
    setMessage(formatted);

    // Filter logs for this ticket
    const allLogs = getLocalSmsLogs();
    const related = allLogs.filter(l => l.ticketId === ticket.id || l.phone === ticket.customerPhone);
    setTicketLogs(related);
  }, [ticket, isOpen]);

  const handleTemplateChange = (key: string) => {
    setSelectedTemplateKey(key);
    if (key === 'empty') {
      setMessage('');
      return;
    }
    const templateText = (settings.templates as any)[key] || '';
    const formatted = formatSmsTemplate(templateText, ticket, companyInfo);
    setMessage(formatted);
  };

  const handleCheckBalance = async () => {
    setCheckingBalance(true);
    setBalanceInfo(null);
    try {
      const res = await checkSmsBalance();
      if (res.success && res.balance) {
        const smsCount = res.balance.sms || res.balance.amount || JSON.stringify(res.balance);
        setBalanceInfo(`Kalan Kredi: ${smsCount} SMS`);
      } else {
        setBalanceInfo(res.message || 'Bakiye sorgulanamadı.');
      }
    } catch (e: any) {
      setBalanceInfo('Hata: ' + e.message);
    } finally {
      setCheckingBalance(false);
    }
  };

  const handleSend = async () => {
    if (!settings.apiKey || !settings.apiHash || !settings.sender) {
      setStatusResult({
        type: 'error',
        text: 'İletimerkezi API bilgileri tanımlanmamış. Lütfen Ayarlar > SMS sekmesinden bilgilerinizi kaydediniz.',
      });
      return;
    }

    if (!phone.trim()) {
      setStatusResult({
        type: 'error',
        text: 'Lütfen geçerli bir alıcı telefon numarası giriniz.',
      });
      return;
    }

    if (!message.trim()) {
      setStatusResult({
        type: 'error',
        text: 'Lütfen gönderilecek SMS metnini giriniz.',
      });
      return;
    }

    setSending(true);
    setStatusResult(null);

    try {
      const result = await sendSmsMessage({
        phone: phone.trim(),
        text: message.trim(),
        ticketId: ticket.id,
        customerName: ticket.customerName,
      });

      if (result.success) {
        setStatusResult({
          type: 'success',
          text: `SMS başarıyla iletildi! ${result.orderId ? `(Sipariş No: ${result.orderId})` : ''}`,
        });
        // Refresh ticket logs
        const allLogs = getLocalSmsLogs();
        setTicketLogs(allLogs.filter(l => l.ticketId === ticket.id || l.phone === ticket.customerPhone));
      } else {
        setStatusResult({
          type: 'error',
          text: result.message || 'SMS gönderimi başarısız oldu.',
        });
      }
    } catch (e: any) {
      setStatusResult({
        type: 'error',
        text: 'Bağlantı hatası: ' + e.message,
      });
    } finally {
      setSending(false);
    }
  };

  const charCount = message.length;
  // Estimate SMS parts (standard GSM: 160 chars, Turkish special: approx 153 for multi-part)
  const smsParts = charCount <= 160 ? (charCount > 0 ? 1 : 0) : Math.ceil(charCount / 153);

  const isConfigured = !!(settings.apiKey && settings.apiHash && settings.sender);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-xs">
      <div className="min-h-full flex items-center justify-center p-3 sm:p-4 text-center">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full border border-slate-100 overflow-hidden flex flex-col max-h-[92vh] my-auto text-left">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-700 to-indigo-800 text-white px-5 py-4 flex items-center justify-between shadow-xs">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-base font-bold">Müşteriye SMS Gönder</h3>
                <span className="bg-blue-500/40 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border border-white/20">
                  İletimerkezi API
                </span>
              </div>
              <p className="text-xs text-blue-100 font-medium">
                {ticket.id} • {ticket.customerName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-xl transition text-white/80 hover:text-white cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-5 overflow-y-auto space-y-4 text-slate-700 flex-1">
          {/* Warning if API not configured */}
          {!isConfigured && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-xs space-y-1">
                <p className="font-bold text-amber-900">SMS Ayarları Henüz Yapılmamış</p>
                <p className="text-amber-800">
                  {isTechnician
                    ? 'SMS gönderim servis bilgileri henüz yönetici tarafından yapılandırılmamış. Lütfen yöneticinizle irtibata geçiniz.'
                    : 'İletimerkezi API Key, API Hash ve Başlık bilginiz tanımlı değil. SMS gönderebilmek için lütfen ayarları kaydediniz.'}
                </p>
                {!isTechnician && onOpenSettings && (
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      onOpenSettings();
                    }}
                    className="inline-block mt-1 font-bold text-blue-700 hover:underline cursor-pointer"
                  >
                    ⚙ SMS Ayarlarını Yapılandır →
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Quick Balance Header */}
          {isConfigured && (
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 px-3.5 py-2 rounded-xl text-xs">
              <div className="flex items-center space-x-2">
                <span className="font-bold text-slate-700">Başlık (Sender):</span>
                <span className="font-mono font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                  {settings.sender || 'Belirtilmedi'}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                {balanceInfo && (
                  <span className="font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                    {balanceInfo}
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleCheckBalance}
                  disabled={checkingBalance}
                  className="text-[11px] font-semibold text-slate-600 hover:text-blue-700 flex items-center gap-1 cursor-pointer"
                >
                  <RefreshCw className={`w-3 h-3 ${checkingBalance ? 'animate-spin text-blue-600' : ''}`} />
                  <span>{checkingBalance ? 'Sorgulanıyor...' : 'Bakiye'}</span>
                </button>
              </div>
            </div>
          )}

          {/* Recipient Phone */}
          <div className="space-y-1">
            <label className="block text-xs font-bold text-slate-700">
              Alıcı Telefon Numarası
            </label>
            <div className="relative">
              <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
              <input
                type="text"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="0505 123 45 67"
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-mono"
              />
            </div>
          </div>

          {/* Template Selection */}
          <div className="space-y-1.5">
            <label className="block text-xs font-bold text-slate-700 flex items-center justify-between">
              <span>Hazır Şablon Seçimi</span>
              <span className="text-[11px] font-normal text-slate-500">Değişkenler otomatik doldurulur</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {[
                { key: 'ticketReady', label: 'Cihaz Hazır' },
                { key: 'ticketCreated', label: 'Kayıt Alındı' },
                { key: 'ticketDelivered', label: 'Teslim Edildi' },
                { key: 'ticketPriceApproved', label: 'Fiyat Onayı' },
                { key: 'ticketCancelled', label: 'İptal Edildi' },
                { key: 'empty', label: 'Boş Mesaj' },
              ].map(tpl => (
                <button
                  type="button"
                  key={tpl.key}
                  onClick={() => handleTemplateChange(tpl.key)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border text-left transition cursor-pointer flex items-center justify-between ${
                    selectedTemplateKey === tpl.key
                      ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <span>{tpl.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* SMS Message Textarea */}
          <div className="space-y-1.5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
              <label htmlFor="sms_direct_message_textarea" className="block text-xs font-bold text-slate-700">
                SMS Mesaj Metni
              </label>
              <div className="text-[11px] font-mono text-slate-500 flex items-center space-x-2">
                <span>
                  <strong>{charCount}</strong> karakter
                </span>
                <span>•</span>
                <span className={`font-bold ${smsParts > 1 ? 'text-amber-600' : 'text-slate-600'}`}>
                  {smsParts} SMS
                </span>
              </div>
            </div>

            {/* Quick Insert Buttons */}
            <div className="flex flex-wrap items-center gap-1.5 bg-slate-50 p-2 rounded-xl border border-slate-200">
              <span className="text-[10px] font-bold text-slate-500 mr-1">Hızlı Değer Ekle:</span>
              {[
                { tag: '{MUSTERI_ADI}', label: ticket.customerName || 'Müşteri', short: 'Müşteri Adı' },
                { tag: '{SERVIS_NO}', label: ticket.id, short: 'Servis No' },
                { tag: '{TAKIP_LINKI}', label: directTrackingUrl, short: '🔗 Cihaz Takip Linki' },
                { tag: '{CIHAZ}', label: `${ticket.deviceBrand || ''} ${ticket.deviceModel || ''}`.trim() || ticket.deviceType, short: 'Cihaz' },
                { tag: '{TUTAR}', label: `${ticket.totalAmount || 0} TL`, short: 'Tutar' },
                { tag: '{FIRMA_ADI}', label: companyInfo.name || 'Firma', short: 'Firma Adı' },
              ].map(item => (
                <button
                  type="button"
                  key={item.tag}
                  onClick={() => {
                    const textarea = document.getElementById('sms_direct_message_textarea') as HTMLTextAreaElement | null;
                    const valToInsert = item.label;
                    if (textarea) {
                      const start = textarea.selectionStart ?? message.length;
                      const end = textarea.selectionEnd ?? message.length;
                      const updated = message.substring(0, start) + valToInsert + message.substring(end);
                      setMessage(updated);
                      setTimeout(() => {
                        textarea.focus();
                        textarea.setSelectionRange(start + valToInsert.length, start + valToInsert.length);
                      }, 0);
                    } else {
                      setMessage(prev => (prev ? `${prev} ${valToInsert}` : valToInsert));
                    }
                  }}
                  title={`"${item.label}" metnini ekle`}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold shadow-2xs transition-all cursor-pointer ${
                    item.tag === '{TAKIP_LINKI}'
                      ? 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 font-bold'
                      : 'bg-white hover:bg-blue-50 text-blue-700 hover:text-blue-800 border border-slate-200 hover:border-blue-300'
                  }`}
                >
                  <span className={item.tag === '{TAKIP_LINKI}' ? 'text-indigo-600 font-bold' : 'text-blue-500 font-bold'}>+</span>
                  <span>{item.short}</span>
                  <span className="text-[10px] text-slate-400 font-mono">({item.tag})</span>
                </button>
              ))}
            </div>

            <textarea
              id="sms_direct_message_textarea"
              rows={5}
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Gönderilecek SMS mesajını buraya yazınız veya yukarıdaki hızlı değişken butonlarını kullanınız..."
              className="w-full p-3 border border-slate-300 rounded-xl text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white font-mono shadow-2xs resize-y"
            />

            {/* Direct Customer Tracking URL Live Card */}
            <div className="bg-indigo-50/70 border border-indigo-200/80 rounded-xl p-2.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 font-bold text-indigo-950 text-[11px]">
                  <span>🔗 Müşteri Doğrudan Takip Linki:</span>
                  <span className="text-[10px] text-indigo-600 font-normal">(Form &amp; GSM Otomatik)</span>
                </div>
                <div className="font-mono text-[10px] text-indigo-800 truncate select-all mt-0.5">
                  {directTrackingUrl}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={handleCopyDirectTrackingLink}
                  className="px-2 py-1 bg-white hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-lg text-[11px] font-bold flex items-center gap-1 transition cursor-pointer"
                  title="Doğrudan takip linkini panoya kopyala"
                >
                  {copiedTrackingLink ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                  <span>{copiedTrackingLink ? 'Kopyalandı' : 'Kopyala'}</span>
                </button>
                <button
                  type="button"
                  onClick={handleOpenDirectTracking}
                  className="px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-[11px] font-bold flex items-center gap-1 transition cursor-pointer"
                  title="Müşteri takip sayfasını yeni sekmede test et"
                >
                  <ExternalLink className="w-3 h-3" />
                  <span>Test Et</span>
                </button>
              </div>
            </div>
          </div>

          {/* Status Message / Result */}
          {statusResult && (
            <div
              className={`p-3 rounded-xl border text-xs font-semibold flex items-start space-x-2 ${
                statusResult.type === 'success'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : 'bg-rose-50 border-rose-200 text-rose-900'
              }`}
            >
              {statusResult.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
              )}
              <span>{statusResult.text}</span>
            </div>
          )}

          {/* Past SMS Logs Toggle */}
          {ticketLogs.length > 0 && (
            <div className="border-t border-slate-200 pt-3">
              <button
                type="button"
                onClick={() => setShowLogs(!showLogs)}
                className="text-xs font-bold text-slate-600 hover:text-slate-900 flex items-center gap-1.5 cursor-pointer"
              >
                <History className="w-3.5 h-3.5" />
                <span>Bu Cihaza Gönderilmiş SMS Geçmişi ({ticketLogs.length})</span>
              </button>

              {showLogs && (
                <div className="mt-2 space-y-1.5 max-h-36 overflow-y-auto">
                  {ticketLogs.map(log => (
                    <div
                      key={log.id}
                      className="bg-slate-50 p-2 rounded-lg border border-slate-200 text-[11px] space-y-0.5"
                    >
                      <div className="flex justify-between items-center text-slate-500">
                        <span>{formatDateTR(log.date)}</span>
                        <span
                          className={`font-bold px-1.5 py-0.2 rounded text-[10px] ${
                            log.status === 'success' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                          }`}
                        >
                          {log.status === 'success' ? 'Başarılı' : 'Hata'}
                        </span>
                      </div>
                      <p className="text-slate-800 font-medium">{log.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer Actions */}
        <div className="bg-slate-50 px-5 py-3.5 border-t border-slate-200 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-xl transition cursor-pointer"
          >
            Kapat
          </button>

          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !phone.trim() || !message.trim()}
            className={`px-5 py-2.5 rounded-xl text-xs font-bold text-white shadow-md flex items-center space-x-2 transition cursor-pointer ${
              sending || !phone.trim() || !message.trim()
                ? 'bg-slate-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 active:scale-95 shadow-blue-500/20'
            }`}
          >
            <Send className={`w-3.5 h-3.5 ${sending ? 'animate-bounce' : ''}`} />
            <span>{sending ? 'Gönderiliyor...' : 'SMS Gönder'}</span>
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};
